import React, { useEffect } from 'react';
import { Collapse, Divider, Form, Switch, Typography, message } from 'antd';
import { ENV } from '@/configs/env.config';
import { useStore } from '@/stores';
import { observer } from 'mobx-react-lite';
import './Settings.scss';

const { Title } = Typography;
const { Panel } = Collapse;
const { Item, useForm } = Form;

interface IFormSettingsValues {
  wrapUpTime: number;
  callInboundNotification: boolean;
  messageIncomingNotification: boolean;
}

const secondsLimit = {
  max: 600,
  min: 1,
};

export const Settings = observer(() => {
  const {
    settingsStore: { dialer, notification, setSettings },
  } = useStore();
  const [form] = useForm<IFormSettingsValues>();

  const checkMessageNotification = async (messageNotification: boolean) => {
    if (!messageNotification) {
      setSettings({
        notification: { ...notification, incomingMessage: false },
      });
      message.success('Incoming message notification is off', 1);
      return;
    }

    if (!('Notification' in window)) {
      // Check if the browser supports notifications
      setSettings({
        notification: { inboundCall: false, incomingMessage: false },
      });
      message.error('Browser has no notification support', 1);

      return;
    }

    if (Notification.permission === 'granted') {
      setSettings({ notification: { ...notification, incomingMessage: true } });
      message.success('Incoming message notification is on', 1);

      return;
    }

    const result = await Notification.requestPermission();

    if (result === 'granted') {
      setSettings({ notification: { ...notification, incomingMessage: true } });
      message.success('Incoming call notification is on', 1);

      return;
    }

    message.warning('Please turn on browser notifications first', 2);
    setSettings({ notification: { ...notification, incomingMessage: false } });
  };

  const checkCallNotification = async (callInboundNotification: boolean) => {
    if (!callInboundNotification) {
      setSettings({ notification: { ...notification, inboundCall: false } });
      message.success('Incoming call notification is off', 1);
      return;
    }

    if (!('Notification' in window)) {
      // Check if the browser supports notifications
      setSettings({
        notification: { inboundCall: false, incomingMessage: false },
      });
      message.error('Browser has no notification support', 1);

      return;
    }

    if (Notification.permission === 'granted') {
      setSettings({ notification: { ...notification, inboundCall: true } });
      message.success('Incoming call notification is on', 1);

      return;
    }

    const result = await Notification.requestPermission();

    if (result === 'granted') {
      setSettings({ notification: { ...notification, inboundCall: true } });
      message.success('Incoming call notification is on', 1);

      return;
    }

    message.warning('Please turn on browser notifications first', 2);
    setSettings({ notification: { ...notification, inboundCall: false } });
  };

  useEffect(() => {
    form.setFieldValue('wrapUpTime', dialer.wrapUpTime / 1000);
  }, [dialer]);

  useEffect(() => {
    form.setFieldsValue({
      callInboundNotification: notification.inboundCall,
      messageIncomingNotification: notification.incomingMessage,
    });
  }, [notification]);

  const onValuesChange = ({
    wrapUpTime,
    callInboundNotification,
    messageIncomingNotification,
  }: Partial<IFormSettingsValues>) => {
    if (typeof callInboundNotification === 'boolean') {
      checkCallNotification(callInboundNotification);
    }

    if (typeof messageIncomingNotification === 'boolean') {
      checkMessageNotification(messageIncomingNotification);
    }

    if (
      !wrapUpTime ||
      wrapUpTime > secondsLimit.max ||
      wrapUpTime < secondsLimit.min
    ) {
      return;
    }
  };

  return (
    <Form
      form={form}
      className="settings"
      name="form"
      layout="vertical"
      onValuesChange={onValuesChange}
    >
      <div className="settings__header">
        <Title level={5}>Settings</Title>
      </div>
      <div className="settings__content">
        <Divider orientation="left">Version: {ENV.APP_VERSION}</Divider>
        <Collapse>
          {/* <Panel
            header="Dialer"
            key="1"
            extra={
              <Button
                disabled={savingWrapUpDisabled}
                htmlType="button"
                type="dashed"
                shape="circle"
                onClick={onSaveWrapUpTime}
                icon={<SaveOutlined />}
              />
            }
          >
            <Item
              name="wrapUpTime"
              label="Wrap Up Time:"
              rules={[
                {
                  type: 'number',
                  min: secondsLimit.min,
                  max: secondsLimit.max,
                  message: `Wrap Up Time must be between ${secondsLimit.min} and ${secondsLimit.max}`,
                },
                { required: true, message: 'Wrap Up Time is required' },
              ]}
            >
              <InputNumber maxLength={4} addonAfter={'seconds'} />
            </Item>
          </Panel> */}
          <Panel header="Notifications" key="2">
            <Item
              name="callInboundNotification"
              label="Incoming call notification:"
              valuePropName="checked"
            >
              <Switch />
            </Item>
            <Item
              name="messageIncomingNotification"
              label="Incoming message notification:"
              valuePropName="checked"
            >
              <Switch />
            </Item>
          </Panel>
        </Collapse>
      </div>
    </Form>
  );
});
